export default {
  title: 'API Services',
  img: 'services',
  subsections: [
    {
      title: 'Auth Service',
      content: [
        {
          tag: 'highlight',
          content: 'Description'
        },
        {
          tag: 'p',
          content: `The <b>Auth</b> (authorization) service is used to exchange a user’s credentials (the ones also used for the BreeziPro
            PWA: email and password) for a response which includes the account’s API Key.`
        },
        {
          tag: 'highlight',
          content: 'Request Structure'
        },
        {
          tag: 'p',
          content: 'POST request to https://api-pro.breezi.net/auth'
        },
        {
          tag: 'p',
          content: 'Request Body:'
        },
        {
          tag: 'code',
          content: JSON.stringify({
            email: "{yourEmail}",
            password: "{yourPassword}"
          }, null, 3)
        },
        {
          tag: 'highlight',
          content: 'Response Structure:'
        },
        {
          tag: 'code',
          content: JSON.stringify({
            "user": "{yourName}",
            "organization": "{yourOrganizationName}",
            "apiKey": "{yourAPIKey}"
          }, null, 3)
        },

      ]
    },

    {
      title: 'Account Service',
      content: [
        {
          tag: 'highlight',
          content: 'Description'
        },
        {
          tag: 'p',
          content: `The <b>Account</b> service is used to access core information about your organization within the BreeziPro Commercial
          API ecosystem. From here you can track the number of requests you have made during the current
          billing cycle; a value that can also be accessed from previous cycles. Additionally, this service can be modified using the
          <b>?ids</b> parameter to return a list of Unique Building Ids and their included AirPulse Serial Numbers (APSNs).`
        },
        {
          tag: 'highlight',
          content: 'Request Structure'
        },
        {
          tag: 'p',
          content: 'GET request to https://api-pro.breezi.net/account'
        },
        {
          tag: 'highlight',
          content: 'Parameters'
        },
        {
          tag: 'p',
          content: 'Query Parameters'
        },
        {
          tag: 'ul',
          content: [
            '?ids={boolean} - Returns all Buildings and their Unique ids, as well as the Serial Numbers of all AirPulses installed therein.',
            '?ids={buildingId} - Returns the Unique id for the requested building and only the Serial Numbers of the included AirPulse devices.'
          ]
        },
        {
          tag: 'highlight',
          content: 'Response Structure:'
        },
        {
          tag: 'code',
          content: JSON.stringify({
            "org": "{yourOrganizationName}",
            "buildings": [
              {
                "id": "{uniqueBuildingId}",
                "building": "{buildingName}",
                "address": "{buildingAddress}",
                "floors": {
                  "Floor1": [
                    {
                      "apsn": "{airpulseSerialNumber}",
                      "name": "{datapointNameWhereInstalled}"
                    },
                    "airpulse2",
                    "airpulse3"
                  ],
                  "Floor2": [],
                  "Floor3" : []
                }
              },
              "building2",
              "building3"
            ]
          }, null, 3)
        },
      ]
    },

    {
      title: 'Alerts Service',
      content: [
        {
          tag: 'highlight',
          content: 'Description'
        },
        {
          tag: 'p',
          content: `A call to the <b>Alerts</b> Service will render a list of alert status changes for a requested device. This
          information can be used independently in order to assess patterns in measurement fluctuation and potentially shed
          light on potential causes of change in environmental conditions. Additionally, this service is intended to be used in
          conjunction with the /alerts Service, described below, to present more granular information in regards to conditions at the
          time an alert or series of alerts were recorded.`
        },
        {
          tag: 'highlight',
          content: 'Request Structure'
        },
        {
          tag: 'p',
          content: 'GET request to https://api-pro.breezi.net/alerts'
        },
        {
          tag: 'highlight',
          content: 'Parameters'
        },
        {
          tag: 'p',
          content: 'Path Parameters'
        },
        {
          tag: 'ul',
          content: [
            '/{apsn} - AirPulse serial number for the device being monitored'
          ]
        },
        {
          tag: 'compat',
          label: `<u>Query Parameter Compatibility Guide</u><br/>
            <span style="font-size:.9em"><span style="color:rgb(111, 191, 91)">Green</span>: Params can be used together in the same request
            <br/><span style="color:rgb(168, 68, 68)">Red</span>: Params are incompatible</span>`,
          content: compatTable('from', 'to', 'tms', 'include', 'omit', 'limit', 'cycle')
        },
        {
          tag: 'p',
          content: 'Query Parameters'
        },
        {
          tag: 'ul',
          content: [
            'If no query parameters are included in request url, by default the service will return the most recent 50 days of recorded alerts',
            '?from={tmsInt} - sets the starting timestamp for a request for a range of data. Used alone, this parameter will indicate to the service that it should return the day found at the requested timestamp, and the subsequent 50 days of alert data to follow',
            '?to={tmsInt} - sets the ending timestamp for a request for a range of data. Used alone, this parameter will indicate to the service that it should return the day found at the requested timestamp, and the preceding 50 days of alert data up to that point',
            '?tms={tmsInt} - sets the requested timestamp as the query’s midpoint and returns the preceding 25 days and following 25 days of alert data for requested AirPulse device',
            '?include={listOfReadings} - returns only alerts of the reading types included in the parameter value, which are separated by "+". Possible reading keywords are: temp, hum, co2, iaq, voc, bat',
            '?omit={listOfReadings} - returns only alerts of the types NOT included in the parameter value',
            '?limit={numberOfReadings} - returns only the requested number of alert records. Typically, one alert record is equal to one day of data.',
            `?cycle={tmsInt} - <b>COMING SOON</b> Using timestamp as a midpoint, this parameter will indicate to the service that it should return all measurement data
            from the beginning of the transition into the given alert status (by reading) until its transition to a new alert status, which is typically only a few
            minutes but can last days or longer if conditions around the AirPulse device’s datapoint are particularly transient in nature.`
          ]
        },
        {
          tag: 'png',
          label: 'How measurements are collected when using <span style="color:rgb(0, 153, 191)">?cycle</span> Query Parameter',
          content: 'cycle'
        },
        {
          tag: 'highlight',
          content: 'Response Structure:'
        },
        {
          tag: 'p',
          content: 'For RANGE'
        },
        {
          tag: 'code',
          content: JSON.stringify({
           "apsn": "{airpulseSerialNumber}",
           "alerts": {
            "htp": [
              {
                "tmsRange": [
                  "start",
                  "end"
                ],
                "packetRange": [
                  "start",
                  "end"
                ],
                "tier": "bad/crit",
                "extreme": "highest or lowest reading",
                "change": "escalated/deescalated/persisted"
              },
              "htpAlert2",
              "htpAlert3"
            ],
            "hhm": [
              "alerts..."
            ],
            "voc": [
              "alerts..."
            ],
            "iaq": [
              "alerts..."
            ],
            "co2": [
              "alerts..."
            ]
           }
          }, null, 3)
        },
        {
          tag: 'highlight',
          content: 'Response Structure:'
        },
        {
          tag: 'p',
          content: 'For CYCLE'
        },
        {
          tag: 'code',
          content: JSON.stringify({
            "apsn": "{airpulseSerialNumber}",
            "sensorType": "{envSensorOrFilterSensor}",
            "datapointName": "{name}",
            "rawMeasurements": {
              "tms": ["{parabolaMeasPointStartTms}", '...measurementPointTimestamps', "{parabolaMeasPointEndTimestamp}"],
              "packetTms": ["{parabolaFirstPacketTms}", '...packetTimestamps', "{parabolaEndPacketTimestamp}"],
              "readableTms": ["{formattedDateForMeasPointStarTms}", '...readablePointTimestamps', "{formattedDateForMeasPointEndTms}"],
              "readablePacketTms": ["{formattedDateForMeasPacketStarTms}", '...readablePacketTimestamps', "{formattedDateForMeasPacketEndTms}"],
              "temp": [
                {
                  "value": '{tempValue}',
                  "alertStatus": ["alertTier"]
                }
              ],
              "hum": [],
              "iaqAcc": [],
              "iaq": [],
              "vocAcc": [],
              "voc": []
            }
          }, null, 3)
        }
      ]
    },

    {
      title: 'Measurements Service',
      content: [
        {
          tag: 'highlight',
          content: 'Description'
        },
        {
          tag: 'p',
          content: `This service is intended to give the most low-level view of the conditions within your commercial properties. It allows access to the core datasets as recorded by your AirPulse devices, and can be used to view all raw measurement data within a requested timespan, which can be further reformatted for consumption by other applications, APIs or integrated systems. As mentioned previously, this service can also be used in tandem with the Alerts Service to provide accurate and complete data regarding instances of elevated readings and alert thresholds within your buildings.`
        },
        {
          tag: 'highlight',
          content: 'Request Structure'
        },
        {
          tag: 'p',
          content: 'GET request to https://api-pro.breezi.net/measurements'
        },
        {
          tag: 'highlight',
          content: 'Parameters'
        },
        {
          tag: 'p',
          content: 'Path Parameters'
        },
        {
          tag: 'ul',
          content: [
            '/{apsn} - AirPulse serial number for the device being monitored'
          ]
        },
        {
          tag: 'compat',
          label: `<u>Query Parameter Compatibility Guide</u><br/>
            <span style="font-size:.9em"><span style="color:rgb(111, 191, 91)">Green</span>: Params can be used together in the same request
            <br/><span style="color:rgb(168, 68, 68)">Red</span>: Params are incompatible</span>`,
          content: compatTable('from', 'to', 'tms', 'include', 'omit', 'limit', 'interval', 'alerts')
        },
        {
          tag: 'p',
          content: 'Query Parameters'
        },
        {
          tag: 'ul',
          content: [
            'If no query parameters are included in request url, by default the service will return the most recent 50 recorded Measurement Packets.',
            '?from={tmsInt} - sets the starting timestamp for a request for a range of measurement packets. Used alone, this parameter will indicate to the service that it should return the packet found at the requested timestamp, and the subsequent 50 packets which follow.',
            '?to={tmsInt} - sets the ending timestamp for a request for a range of measurement packets. Used alone, this parameter will indicate to the service that it should return the packet found at the requested timestamp, and the preceding 50 packets',
            '?tms={tmsInt} - sets the requested timestamp as the query’s midpoint and returns the preceding 25 packets and following 25 packets of measurement data for requested AirPulse device',
            '?include={listOfReadings} - returns only data of the reading types included in the parameter value, which are separated by "+".  Additionally, this parameter can be used to query ONLY the filter condition over the requested timespan, which will include the outcomes section of the documented response below, while eliminating the rawMeasurements section. Both are included by default.',
            'Available reading keywords are: temp, hum, co2, iaq, voc, bat',
            '?omit={listOfReadings} - returns only readings of the types NOT included in the parameter value',
            '?limit={numberOfReadings} - returns only the requested number of Measurement Packets',
            '?interval={secondsBetweenMeasurementPackets} - can be used to overwrite the “Smart Querying” utility outlined in the “Introduction” section of this documentation. By enabling this parameter, a user can specify approximately how many seconds will be skipped between each packet returned in the query. Since data is not recorded at even intervals to the second, the service will sometimes need to recursively query increasingly large timespans in order to fill out the response according to the specified interval. Note: if an interval is specified that is larger than the gaps between the actual packets within the requested timestamp range, the service will return a smaller dataset than omitting this parameter and allowing the Smart Query utility to take effect.',
            '?alerts={boolean} - Indicates to the service whether or not each measurement value should be paired with its corresponding alert “tier” string. Disabling this feature makes the rawMeasurement object returned from the API much easier to convert into other templates, such as CSV, as nested key-value pairs will be eliminated',
          ]
        },
        {
          tag: 'highlight',
          content: 'Response Structure:'
        },
        {
          tag: 'code',
          content: JSON.stringify({
            "apsn": "{airpulseSerialNumber}",
            "sensorType": "{envSensorOrFilterSensor}",
            "datapointName": "{name}",
            "outcomes": [
              {
                "type": "Filter Condition",
                "tmsRange": ["{firstDayTrackedTms}", '...daysTracked', "{finalDayTrackedTms}"],
                "daysRemaining": ["{daysRemainingDay1}", '...daysRemainingForTrackedDays', "{daysRemainingFinalDay}"],
                "percentOfLifeRemaining": ["{lifePercentageRemainingDay1}", '...lifePercentageRemainingForTrackedDays', "{lifePercentageRemainingFinalDay}"],
                "deltaPressureForDay":  ["{deltaPressureDay1}", '...dayDeltaPressureVals', "{deltaPressureFinalDay}"]
              }
            ],
            "rawMeasurements": {
              "tms": ["{measPointStartTms}", '...measurementPointTimestamps', "{measPointEndTimestamp}"],
              "packetTms": ["{firstPacketTms}", '...packetTimestamps', "{endPacketTimestamp}"],
              "readableTms": ["{formattedDateForMeasPointStarTms}", '...readablePointTimestamps', "{formattedDateForMeasPointEndTms}"],
              "readablePacketTms": ["{formattedDateForMeasPacketStarTms}", '...readablePacketTimestamps', "{formattedDateForMeasPacketEndTms}"],
              "temp": [
                {
                  "value": '{tempValue}',
                  "alertStatus": ["alertTier"]
                }
              ],
              "hum": [],
              "iaqAcc": [],
              "iaq": [],
              "vocAcc": [],
              "voc": []
            }
          }, null, 3)
        },
      ]
    },

    {
      title: 'Snapshot Service',
      content: [
        {
          tag: 'highlight',
          content: 'Description'
        },
        {
          tag: 'p',
          content: `This service is intended to give you a contemproary overview of the conditions within your commercial property. Each AirPulse device will be listed in its respective Coverage Area and the measurements displayed will be the latest recorded in Breezi's Database.`
        },
        {
          tag: 'highlight',
          content: 'Request Structure'
        },
        {
          tag: 'p',
          content: 'GET request to https://api-pro.breezi.net/snapshot'
        },
        {
          tag: 'highlight',
          content: 'Parameters'
        },
        {
          tag: 'p',
          content: 'Path Parameters'
        },
        {
          tag: 'ul',
          content: [
            '/{entityId} - AirPulse Serial Number OR Unique Building Id'
          ]
        },
        {
          tag: 'p',
          content: 'Query Parameters'
        },
        {
          tag: 'ul',
          content: [
            '?floor - Allows you to specify the floor for which you would like data returned (only compatible with <b>/buildingId</b> Path Parameter)',
            '?tms - Allows you to specifry the timestamp at which data should be queried for each device',
            '?include - All readings included in this list (separated by "+") will be returned in the response',
            '?omit - All readings included in this list (separated by "+") will be ignored in the response'
          ]
        },
        {
          tag: 'code',
          content: JSON.stringify({
            "ahuSensor": {
              "apsn":"{ airpulseSerialNumber }",
              "filterLastChanged":"{ tmsOfLastFilterChange }",
              "floor":"{ floorIndexStartingAt1 }",
              "datapointName":"{ nameOfMeasurementArea }",
              "outcomes": [{
                "type":"{typeOfComputedValue}",
                "data": {
                  "tmsRange": ["{ outcomeStartTms }", "{ outcomeEndTms }"],
                  "maxDeltaPressureDay": " { float }",
                  "deltaPressureDay": "{ float }",
                  "daysRemaining":"{ int }",
                  "percentOfLifeRemaining": " { float }"
                }
              }],
              "rawMeasurements": {
                "tmsRange": ["{ measurementPacketStartTms }","{ measurementPacketStartTms }"],
                "temp": ["{ currentTemperature }"],
                "hum": ["{ realtiveHumdiityPercentage }"],
                "iaq": ["{ indoorAirQualityIndex }"],
                "iaqAcc": ["{ iaqAccuracy }"],
                "tvocAcc": ["{ tvocAcuracy }"],
                "tvoc": ["{ tvocPPM }"],
                "tDeltaPressure": ["{ int }"]
              }
            },
            "envSensors": {
              "apsn":"{ str }",
              "floor":"{ floorIndexStartingAt1 }",
              "datapointName":"{ nameOfMeasurementArea }",
              "outcomes": [],
              "rawMeasurements": {
                "tmsRange": ["{ measurementPacketStartTms }","{ measurementPacketStartTms }"],
                "temp": ["{ currentTemperature }"],
                "hum": ["{ realtiveHumdiityPercentage }"],
                "iaq": ["{ indoorAirQualityIndex }"],
                "iaqAcc": ["{ iaqAccuracy }"],
                "tvocAcc": ["{ tvocAcuracy }"],
                "tvoc": ["{ tvocPPM }"],
                "tDeltaPressure": ["{ int }"]
              }
            }
          }, null, 3)
        }
      ]
    }
  ]
}

function compatTable(...params){
  const compatibilities = {
    from: [
      'to',
      'include',
      'omit',
      'limit',
      'alerts',
      'interval'
    ],
    to: [
      'from',
      'include',
      'omit',
      'limit',
      'alerts',
      'interval'
    ],
    tms: [
      'include',
      'omit',
      'limit',
      'alerts',
      'interval'
    ],
    include: [
      'from',
      'to',
      'tms',
      'limit',
      'cycle',
      'alerts',
      'interval'
    ],
    omit: [
      'from',
      'to',
      'tms',
      'limit',
      'cycle',
      'alerts',
      'interval'
    ],
    limit: [
      'from',
      'to',
      'tms',
      'include',
      'omit',
      'cycle',
      'alerts',
      'interval'
    ],
    cycle: [
      'include',
      'omit',
      'limit',
      'interval',
      'alerts'
    ],
    interval: [
      'from',
      'to',
      'tms',
      'include',
      'omit',
      'limit',
      'alerts',
    ],
    alerts: [
      'from',
      'to',
      'tms',
      'include',
      'omit',
      'limit',
      'interval',
      'cycle'
    ]
  };
  const preFormat = params.map(p => ({tag: 'txt', val: p}));
  const format = params.map((p1, p1x) => {
    return params.map((p2, p2x) => {
      if (p1 == p2) {
        return {tag: 'clr', val: 'darkgray'}
      } else {
        return compatibilities[p1].includes(p2)
          ? {tag: 'clr', val: 'rgb(111, 191, 91)'}
          : {tag: 'clr', val: 'rgb(168, 68, 68)'};
      }
    });
  });
  format.forEach((frmt, frmtX) => {
    frmt.unshift(preFormat[frmtX]);
  });
  preFormat.unshift({tag:'txt', val: 'PARAM'});
  format.unshift(preFormat);
  return format;
}



// WEBPACK FOOTER //
// ./src/lib/apiDocTemplates/services.js